'use client';

import { useTranslation } from 'react-i18next';
import type { MicroDownloadBody } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { PopupHeader, padded } from '../chrome';
import { DownloadIcon } from '../icons';

/** 256000 → "250 KB"; sub-KB byte counts stay in bytes. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 'application/pdf' → 'PDF'. The subtype is the readable half. */
function mimeLabel(mimeType: string): string {
  const subtype = mimeType.split('/')[1];
  return (subtype ?? mimeType).toUpperCase();
}

/**
 * Single-file download card over the canonical {@code MicroDownloadBody}
 * ({@code fileUrl} + {@code fileName}, best-effort size / mime /
 * preview metadata). The download click fires {@code onComplete}.
 */
export function DownloadRenderer({ item, onComplete }: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const body = (item.body ?? {}) as MicroDownloadBody;

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <div style={{ ...padded, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {body.description && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              lineHeight: 1.6,
              color: 'var(--color-text-primary, #18181b)',
            }}
          >
            {body.description}
          </p>
        )}
        {!body.fileUrl ? (
          <div style={{ color: 'var(--color-text-secondary, #71717a)' }}>
            {t('microDownload.empty')}
          </div>
        ) : (
          <a
            href={body.fileUrl}
            download={body.fileName ?? ''}
            aria-label={t('microDownload.buttonAria', { name: body.fileName || item.title })}
            onClick={() => onComplete()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              padding: 14,
              border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
              borderRadius: 8,
              textDecoration: 'none',
              color: 'inherit',
              background: 'var(--color-surface-card, #fff)',
            }}
          >
            {body.previewImageUrl ? (
              <img
                src={body.previewImageUrl}
                alt=""
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 6,
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            ) : (
              <DownloadIcon />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600 }}>
                {body.fileName || item.title}
              </div>
              {(body.mimeType || body.fileSizeBytes != null) && (
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: 'var(--color-text-tertiary, #94867d)',
                    fontFamily: 'var(--font-family-mono, monospace)',
                  }}
                >
                  {body.mimeType && mimeLabel(body.mimeType)}
                  {body.mimeType && body.fileSizeBytes != null && ' · '}
                  {body.fileSizeBytes != null && formatBytes(body.fileSizeBytes)}
                </div>
              )}
            </div>
            {/* The whole card is the link, but a card doesn't read as a
                download — the explicit button does. */}
            <span
              aria-hidden
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
                padding: 'var(--space-2, 8px) var(--space-4, 16px)',
                borderRadius: 8,
                background: 'var(--color-brand-500, #3b5bdb)',
                color: 'var(--color-text-on-brand, #fff)',
                fontSize: '13px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M5 21h14" />
              </svg>
              {t('microDownload.button')}
            </span>
          </a>
        )}
      </div>
    </>
  );
}
